import React, { useState, useEffect } from 'react';
import { 
  Settings as SettingsIcon, 
  Shield, 
  Bell, 
  Database, 
  Mail, 
  Globe, 
  Lock, 
  Eye, 
  EyeOff,
  Save,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Phone
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from '../common/AdminLayout';

interface SettingsData {
  general: {
    siteName: string;
    siteDescription: string;
    contactEmail: string;
    supportPhone: string;
    timeZone: string;
    language: string;
    maintenanceMode: boolean;
  };
  security: {
    enableTwoFactor: boolean;
    passwordExpiry: number;
    maxLoginAttempts: number;
    sessionTimeout: number;
    enforceStrongPasswords: boolean;
  };
  notifications: {
    emailNotifications: boolean;
    smsNotifications: boolean;
    pushNotifications: boolean;
    adminAlerts: boolean;
    systemAlerts: boolean;
    bookingAlerts: boolean;
  };
 
}

const Settings: React.FC = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  const { admin } = useAuth();

  const [settings, setSettings] = useState<SettingsData>({
    general: {
      siteName: 'SkillSeek',
      siteDescription: 'Connect with skilled talent in Jamaica',
      contactEmail: 'admin@skillseek.com',
      supportPhone: '+1-876-555-0123',
      timeZone: 'America/Jamaica',
      language: 'en',
      maintenanceMode: false,
    },
    security: {
      enableTwoFactor: false,
      passwordExpiry: 90,
      maxLoginAttempts: 5,
      sessionTimeout: 30,
      enforceStrongPasswords: true,
    },
    notifications: {
      emailNotifications: true,
      smsNotifications: false,
      pushNotifications: true,
      adminAlerts: true,
      systemAlerts: true,
      bookingAlerts: true,
    },
   
  });

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      // Simulate loading time
      setTimeout(() => setLoading(false), 1000);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      // Simulate saving
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setMessage({ type: 'success', text: 'Settings saved successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Failed to save settings:', error);
      setMessage({ type: 'error', text: 'Failed to save settings. Please try again.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      setMessage({ type: 'error', text: 'Password must be at least 8 characters long.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      return;
    }

    try {
      setSaving(true);
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setMessage({ type: 'success', text: 'Password changed successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Failed to change password:', error);
      setMessage({ type: 'error', text: 'Failed to change password. Please try again.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (section: keyof SettingsData, field: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const settingsTabs = [
    { id: 'general', label: 'General', icon: Globe },
   
    { id: 'notifications', label: 'Notifications', icon: Bell },
  
  ];

  if (loading) {
    return (
      <AdminLayout title="Settings" subtitle="Configure platform settings and preferences">
        <div className="loading-spinner" />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Settings" subtitle="Configure platform settings and preferences">
      <div className="fade-in">
        {/* Header Actions */}
        <div className="admin-card" style={{ marginBottom: '25px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <SettingsIcon size={24} />
              <h3 style={{ margin: 0, color: 'var(--text-white)' }}>Platform Settings</h3>
            </div>
            <button
              onClick={handleSaveSettings}
              disabled={saving}
              className="btn-primary"
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              {saving ? (
                <>
                  <RefreshCw size={16} className="spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={16} />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>

        {/* Alert Messages */}
        {message.text && (
          <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-danger'}`} style={{ marginBottom: '25px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {message.type === 'success' ? (
                <CheckCircle size={20} />
              ) : (
                <AlertTriangle size={20} />
              )}
              {message.text}
            </div>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: '25px' }}>
          {/* Settings Navigation */}
          <div className="admin-card">
            <div className="card-header">
              <h4 className="card-title">Settings</h4>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {settingsTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`nav-tab ${activeTab === tab.id ? 'active' : ''}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 16px',
                    border: 'none',
                    borderRadius: '8px',
                    background: activeTab === tab.id ? '#000' : 'transparent',
                    color: activeTab === tab.id ? 'white' : 'var(--text-light-gray)',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    textAlign: 'left',
                    width: '100%'
                  }}
                >
                  <tab.icon size={18} />
                  {tab.label}
                </button>
              ))}
            </div>

        
          </div>

          {/* Settings Content */}
          <div className="admin-card">
            {/* General Settings */}
            {activeTab === 'general' && (
              <div>
                <div className="card-header">
                  <h4 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Globe size={20} />
                    General Settings
                  </h4>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
                  <div className="form-group">
                    <label className="form-label">Site Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={settings.general.siteName}
                      onChange={(e) => handleInputChange('general', 'siteName', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Contact Email</label>
                    <input
                      type="email"
                      className="form-control"
                      value={settings.general.contactEmail}
                      onChange={(e) => handleInputChange('general', 'contactEmail', e.target.value)}
                    />
                  </div>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label className="form-label">Site Description</label>
                    <textarea
                      className="form-control"
                      rows={3}
                      value={settings.general.siteDescription}
                      onChange={(e) => handleInputChange('general', 'siteDescription', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Support Phone</label>
                    <input
                      type="tel"
                      className="form-control"
                      value={settings.general.supportPhone}
                      onChange={(e) => handleInputChange('general', 'supportPhone', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Time Zone</label>
                    <select
                      className="form-control"
                      value={settings.general.timeZone}
                      onChange={(e) => handleInputChange('general', 'timeZone', e.target.value)}
                    >
                      <option value="America/Jamaica">Jamaica (UTC-5)</option>
                      <option value="America/New_York">Eastern Time (UTC-5/-4)</option>
                      <option value="America/Chicago">Central Time (UTC-6/-5)</option>
                      <option value="America/Denver">Mountain Time (UTC-7/-6)</option>
                      <option value="America/Los_Angeles">Pacific Time (UTC-8/-7)</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Language</label>
                    <select
                      className="form-control"
                      value={settings.general.language}
                      onChange={(e) => handleInputChange('general', 'language', e.target.value)}
                    >
                      <option value="en">English</option>
                      <option value="es">Spanish</option>
                      <option value="fr">French</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={settings.general.maintenanceMode}
                        onChange={(e) => handleInputChange('general', 'maintenanceMode', e.target.checked)}
                      />
                      <label className="form-check-label">
                        Enable Maintenance Mode
                      </label>
                      <div style={{ color: 'var(--text-light-gray)', fontSize: '14px', marginTop: '5px' }}>
                        When enabled, only admins can access the platform
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Security Settings */}
            {activeTab === 'security' && (
              <div>
                <div className="card-header">
                  <h4 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Shield size={20} />
                    Security Settings
                  </h4>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', marginBottom: '30px' }}>
                  <div className="form-group">
                    <label className="form-label">Password Expiry (days)</label>
                    <input
                      type="number"
                      className="form-control"
                      min="30"
                      max="365"
                      value={settings.security.passwordExpiry}
                      onChange={(e) => handleInputChange('security', 'passwordExpiry', parseInt(e.target.value))}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Max Login Attempts</label>
                    <input
                      type="number"
                      className="form-control"
                      min="3"
                      max="10"
                      value={settings.security.maxLoginAttempts}
                      onChange={(e) => handleInputChange('security', 'maxLoginAttempts', parseInt(e.target.value))}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Session Timeout (minutes)</label>
                    <input
                      type="number"
                      className="form-control"
                      min="15"
                      max="480"
                      value={settings.security.sessionTimeout}
                      onChange={(e) => handleInputChange('security', 'sessionTimeout', parseInt(e.target.value))}
                    />
                  </div>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={settings.security.enableTwoFactor}
                          onChange={(e) => handleInputChange('security', 'enableTwoFactor', e.target.checked)}
                        />
                        <label className="form-check-label">
                          Enable Two-Factor Authentication
                        </label>
                      </div>
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={settings.security.enforceStrongPasswords}
                          onChange={(e) => handleInputChange('security', 'enforceStrongPasswords', e.target.checked)}
                        />
                        <label className="form-check-label">
                          Enforce Strong Passwords
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Change Password Form */}
                <div style={{ borderTop: '1px solid var(--border-medium)', paddingTop: '25px' }}>
                  <h5 style={{ color: 'var(--text-white)', marginBottom: '20px' }}>Change Password</h5>
                  <form onSubmit={handlePasswordChange}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
                      <div className="form-group">
                        <label className="form-label">Current Password</label>
                        <div style={{ position: 'relative' }}>
                          <input
                            type={showPassword ? 'text' : 'password'}
                            className="form-control"
                            value={passwordForm.currentPassword}
                            onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            style={{
                              position: 'absolute',
                              right: '12px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              background: 'none',
                              border: 'none',
                              color: 'var(--text-light-gray)',
                              cursor: 'pointer'
                            }}
                          >
                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                      </div>
                      <div className="form-group">
                        <label className="form-label">New Password</label>
                        <input
                          type="password"
                          className="form-control"
                          value={passwordForm.newPassword}
                          onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Confirm New Password</label>
                        <input
                          type="password"
                          className="form-control"
                          value={passwordForm.confirmPassword}
                          onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <button
                          type="submit"
                          disabled={saving}
                          className="btn-primary"
                        >
                          {saving ? 'Changing...' : 'Change Password'}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Notification Settings */}
            {activeTab === 'notifications' && (
              <div>
                <div className="card-header">
                  <h4 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Bell size={20} />
                    Notification Settings
                  </h4>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '30px' }}>
                  <div>
                    <h5 style={{ color: 'var(--text-white)', marginBottom: '20px' }}>Communication Channels</h5>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={settings.notifications.emailNotifications}
                          onChange={(e) => handleInputChange('notifications', 'emailNotifications', e.target.checked)}
                        />
                        <label className="form-check-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Mail size={16} />
                          Email Notifications
                        </label>
                      </div>
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={settings.notifications.smsNotifications}
                          onChange={(e) => handleInputChange('notifications', 'smsNotifications', e.target.checked)}
                        />
                        <label className="form-check-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Phone size={16} />
                          SMS Notifications
                        </label>
                      </div>
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={settings.notifications.pushNotifications}
                          onChange={(e) => handleInputChange('notifications', 'pushNotifications', e.target.checked)}
                        />
                        <label className="form-check-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Bell size={16} />
                          Push Notifications
                        </label>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h5 style={{ color: 'var(--text-white)', marginBottom: '20px' }}>Alert Types</h5>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={settings.notifications.adminAlerts}
                          onChange={(e) => handleInputChange('notifications', 'adminAlerts', e.target.checked)}
                        />
                        <label className="form-check-label">
                          Admin Alerts
                        </label>
                      </div>
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={settings.notifications.systemAlerts}
                          onChange={(e) => handleInputChange('notifications', 'systemAlerts', e.target.checked)}
                        />
                        <label className="form-check-label">
                          System Alerts
                        </label>
                      </div>
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={settings.notifications.bookingAlerts}
                          onChange={(e) => handleInputChange('notifications', 'bookingAlerts', e.target.checked)}
                        />
                        <label className="form-check-label">
                          Booking Alerts
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

        
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Settings; 